// The venue record, as entries of the Venue profile group (spec/13 §13; #403).
//
// A venue is a resource, not a registry setting: `GET/PATCH /v1/organizations/{o}/venues/{v}`, one
// ETag, `If-Match` required, `412` on a stale write (spec/08 §2.5). Each editable field is one entry,
// and its control is built from the contract's own schema for the field (`UpdateVenue`, picked out of
// the OpenAPI document at build time — vite/contract-schemas.ts), so the renderer draws it exactly as
// it draws a registry setting.

import type { ApiClient, UpdateVenue, Venue, VenuePhotos } from '@clavian/api-client';
import { $defs } from 'virtual:clavian/contract-schemas';

import { versioned, type Versioned } from '../api/versioned';
import { DEFAULT_COUNTRY } from '../i18n';
import { ALL_DAY_WHEN_EQUAL, CUSTOM_CONTROL, type JsonSchema, type SchemaContext } from '../settings/schema';
import type { RenderableSetting, SettingChange, SettingsPart } from '../settings/types';
import { FIELDS, type VenueField } from './fields';

export type { VenueField } from './fields';

function updateVenueSchema(): JsonSchema {
  const schema = $defs['UpdateVenue'];
  if (schema === undefined) throw new Error('The contract has no UpdateVenue schema (vite/contract-schemas.ts).');
  return schema;
}

function timezones(current: string): string[] {
  const known = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
  return known.includes(current) ? known : [current, ...known];
}

/** Presentation hints for the contract's named schemas (see `SchemaContext.hints`). */
function contextFor(venue: Venue): SchemaContext {
  return {
    defs: $defs,
    hints: {
      Timezone: { enum: timezones(venue.timezone) },
      // An opening interval whose two times are equal is open all day (the contract's
      // `OpeningInterval`); a time range in general is not, so the screen says so.
      OpeningInterval: { [ALL_DAY_WHEN_EQUAL]: true },
    },
    defaultCountry: DEFAULT_COUNTRY,
    timezone: venue.timezone,
  };
}

export interface VenueWords {
  readonly label: (field: VenueField) => string;
  readonly section: (section: string) => string;
  readonly setupOnly: string;
  /**
   * What a cutoff change does once the venue trades (ADR-0029) — from the next business day, today and past days keeping
   * theirs — and, after one, which cutoff runs from when.
   */
  readonly cutoffWhileTrading: (venue: Venue) => string;
}

export function venueEntries(venue: Venue, canEdit: boolean, words: VenueWords): RenderableSetting[] {
  const properties = (updateVenueSchema()['properties'] ?? {}) as Record<string, JsonSchema>;
  const context = contextFor(venue);
  return FIELDS.map((field) => {
    const base = properties[field.key] ?? {};
    const lockedByStatus = 'setupOnly' in field && field.setupOnly && venue.status !== 'setup';
    return {
      key: field.key,
      label: words.label(field.key),
      section: words.section(field.section),
      schema: 'control' in field ? { ...base, [CUSTOM_CONTROL]: field.control } : base,
      context,
      // Photos and the logo are shown with their images; everything else is the field as the venue has it.
      value: field.key === 'logoFileId' ? venue.logo : venue[field.key],
      hasOverride: false,
      editable: canEdit && !lockedByStatus,
      ...(field.key === 'businessDayCutoff' && venue.status !== 'setup' ? { description: words.cutoffWhileTrading(venue) } : {}),
      // #403 rule 4: the reason is shown whoever is looking, not only to an editor.
      ...(lockedByStatus ? { readOnlyReason: words.setupOnly } : {}),
    };
  });
}

/** `VenuePhotos` (what the screen shows) → `VenuePhotoRefs` (what an edit sends): the files by id. */
export function photoRefs(photos: VenuePhotos): { cover: string | null; gallery: string[] } {
  return { cover: photos.cover?.fileId ?? null, gallery: photos.gallery.flatMap((photo) => (photo === null ? [] : [photo.fileId])) };
}

export function readVenue(api: ApiClient, organizationId: string, venueId: string): Promise<Versioned<Venue>> {
  return versioned((onResponse) => api.getVenue({ path: { organizationId, venueId }, onResponse }));
}

export function writeVenue(
  api: ApiClient,
  organizationId: string,
  venueId: string,
  changes: readonly SettingChange[],
  etag: string,
): Promise<Versioned<Venue>> {
  const body: Record<string, unknown> = {};
  for (const change of changes) {
    // A resource field has nothing to inherit, so a change is always a value.
    body[change.key] =
      change.key === 'photos'
        ? photoRefs(change.value as VenuePhotos)
        : change.key === 'logoFileId'
          ? ((change.value as { fileId?: string } | null)?.fileId ?? null)
          : change.value;
  }
  return versioned((onResponse) =>
    api.updateVenue({
      path: { organizationId, venueId },
      headerParams: { 'if-match': etag },
      body: body as UpdateVenue,
      onResponse,
    }),
  );
}

export function venuePart(options: {
  readonly api: ApiClient;
  readonly organizationId: string;
  readonly venueId: string;
  readonly venue: Versioned<Venue>;
  readonly canEdit: boolean;
  readonly words: VenueWords;
  /** Told of every fresh version, so the query cache holds what the form holds. */
  readonly onVersion: (venue: Versioned<Venue>) => void;
}): SettingsPart {
  const { api, organizationId, venueId, canEdit, words, onVersion } = options;
  const entriesOf = (venue: Versioned<Venue>) => ({ data: venueEntries(venue.data, canEdit, words), etag: venue.etag });
  return {
    id: 'venue',
    entries: venueEntries(options.venue.data, canEdit, words),
    etag: options.venue.etag,
    save: async (changes, etag) => {
      const next = await writeVenue(api, organizationId, venueId, changes, etag);
      onVersion(next);
      return entriesOf(next);
    },
    reload: async () => {
      const next = await readVenue(api, organizationId, venueId);
      onVersion(next);
      return entriesOf(next);
    },
    // The server files a venue's field errors under the field's own path (`address.line1`), which is
    // how the renderer files them too.
    fieldKey: (field) => field,
    problemField: (code) => (code === 'slug_taken' ? 'slug' : undefined),
  };
}
